// SQLQuery.js
import { useState, useEffect, useRef, useMemo, lazy, Suspense } from "react";
import { useGlobalState } from "./GlobalStateContext";
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./SQLQuery.css";
import { secureStorage } from "./utils/secureStorage";
import ReactMarkdown from 'react-markdown';


ChartJS.register(
  BarElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const Chart = lazy(() => import("react-chartjs-2").then((mod) => ({ default: mod.Chart })));

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

const makeAuthorizedRequest = async (config) => {
  try {
    const token = secureStorage.getItem("token");
    const headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };

    const response = await fetch(`${API_BASE_URL}${config.url}`, {
      ...config,
      headers,
    });

    if (response.status === 401) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        const retryToken = secureStorage.getItem("token");
        const retryHeaders = {
          ...config.headers,
          Authorization: `Bearer ${retryToken}`,
        };
        return await fetch(`${API_BASE_URL}${config.url}`, {
          ...config,
          headers: retryHeaders,
        });
      }
    }

    return response;
  } catch (err) {
    console.error("Request failed:", err);
    throw err;
  }
};

const attemptTokenRefresh = async () => {
  const refreshToken = secureStorage.getItem("refreshToken");
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) throw new Error("Refresh failed");

    const data = await response.json();
    secureStorage.setItem("token", data.access_token);
    return true;
  } catch (error) {
    console.error("Token refresh failed:", error);
    secureStorage.removeItem("token");
    secureStorage.removeItem("refreshToken");
    window.location.reload();
    return false;
  }
};

const SQLQuery = () => {
  const [query, setQuery] = useState("");
  const [database, setDatabase] = useState("");
  const [databases, setDatabases] = useState([]);
  const { sqlChatHistory, setSqlChatHistory, sessionId, setSessionId } = useGlobalState();
  const [loading, setLoading] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    const storedSessionId = secureStorage.getItem("sessionId");
    const id = storedSessionId || `session-${Math.random().toString(36).substr(2, 9)}`;
    secureStorage.setItem("sessionId", id);
    setSessionId(id);

    const savedHistory = secureStorage.getItem(`sqlChatHistory_${id}`);
    if (savedHistory) {
      try {
        // ✅ Only parse if savedHistory is a valid string and not empty
        if (typeof savedHistory === 'string' && savedHistory.trim() && savedHistory !== 'null' && savedHistory !== 'undefined') {
          setSqlChatHistory(JSON.parse(savedHistory));
        } else if (Array.isArray(savedHistory)) {
          // ✅ If secureStorage already parsed it as an array, use it directly
          setSqlChatHistory(savedHistory);
        } else {
          // ✅ Invalid data, reset to empty array
          console.warn('Invalid SQL chat history data, resetting to empty');
          setSqlChatHistory([]);
        }
      } catch (parseError) {
        console.error('Failed to parse SQL chat history:', parseError, 'Raw data:', savedHistory);
        setSqlChatHistory([]); // ✅ Reset to empty array on parse error
        // ✅ Optionally clean up the corrupted data
        secureStorage.removeItem(`sqlChatHistory_${id}`);
      }
    }

    // const savedHistory = secureStorage.getItem(`sqlChatHistory_${id}`);
    // if (savedHistory) {
    //   setSqlChatHistory(JSON.parse(savedHistory));
    // }
  }, [setSessionId, setSqlChatHistory]);

  useEffect(() => {
    chatRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [sqlChatHistory]);

  useEffect(() => {
    if (sessionId) {
      secureStorage.setItem(`sqlChatHistory_${sessionId}`, JSON.stringify(sqlChatHistory));
    }
  }, [sqlChatHistory, sessionId]);

  useEffect(() => {
    const fetchDatabases = async () => {
      try {
        const response = await makeAuthorizedRequest({
          url: "/admin/databases",
          method: "GET",
        });
        if (!response.ok) throw new Error("Failed to fetch databases");

        const data = await response.json();
        setDatabases(data);
        if (data.length > 0) setDatabase(data[0].name);
      } catch (error) {
        console.error("Database fetch error:", error);
      }
    };

    fetchDatabases();
  }, []);

  const handleQuery = async () => {
    if (!query.trim() || !database) return;

    setSqlChatHistory((prev) => [
      ...prev,
      { type: "user", messageType: "text", messageData: query },
      { type: "bot", messageType: "text", messageData: "Fetching results..." },
    ]);
    setQuery("");
    setLoading(true);

    try {
      const response = await makeAuthorizedRequest({
        url: "/tables_and_charts/",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          database,
          question: query,
        }),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const result = await response.json();

      const responseMessage = result.error
        ? {
            type: "bot",
            messageType: "text",
            messageData: `⚠️ Error: ${result.error}`,
          }
        : {
            type: "bot",
            messageType: "tableChart",
            messageData: {
              columns: result.columns,
              rows: result.data,
              chart_config: result.chart_config || null,
              summary: result.summary || null,
            },
          };

      setSqlChatHistory((prev) => prev.slice(0, -1).concat(responseMessage));
    } catch (error) {
      console.error("Query error:", error);
      setSqlChatHistory((prev) =>
        prev.slice(0, -1).concat({
          type: "error",
          messageType: "text",
          messageData: "❌ Error querying the database.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleQuery();
    }
  };

  const handleExportPDF = async () => {
    const input = document.getElementById("sql-chat-export");
    if (!input) return;

    const originalHeight = input.style.height;
    input.style.height = `${input.scrollHeight}px`;

    await new Promise((resolve) => setTimeout(resolve, 500));

    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      scrollY: -window.scrollY,
      backgroundColor: "#ffffff",
    });

    input.style.height = originalHeight;

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const imgProps = pdf.getImageProperties(imgData);
    const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
    const pageHeight = pdf.internal.pageSize.getHeight();

    let heightLeft = imgHeight;
    let position = 0;

    while (heightLeft > 0) {
      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pageHeight;
      if (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
      }
    }

    pdf.save("chat_output.pdf");
  };

  return (
    <div className="sql-query-container">
      <h2 className="sql-title">Tables & Charts (SQL)</h2>

      <div className="sql-database-select-container">
        <p className="sql-label">Select Database:</p>
        <div className="sql-database-options">
          {databases.map((db) => (
            <label key={db.id} className="sql-database-option">
              <input
                type="radio"
                name="database"
                value={db.name}
                checked={database === db.name}
                onChange={(e) => setDatabase(e.target.value)}
              />
              {db.name}
            </label>
          ))}
        </div>
      </div>

      <div className="sql-export-button" style={{ textAlign: "right", marginBottom: "10px" }}>
        <button onClick={handleExportPDF} className="export-pdf-button">
          📄 Export as PDF
        </button>
      </div>

      <div className="sql-chat-container" id="sql-chat-export">
        {sqlChatHistory.map((msg, index) => {
          return (
            <div key={index} className={`sql-chat-message ${msg.type}`}>
              {msg.messageType === "text" && (
                <p className="whitespace-pre-wrap">{msg.messageData}</p>
              )}

              {msg.messageType === "tableChart" && (
                <div className="sql-chat-response">

                  {/* 🆕 Summary */}

                  {msg.messageData.summary && (
                    <div className="sql-chat-summary mb-4 text-sm text-gray-700 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                      <strong>Insight:</strong>
                      <div className="mt-1">
                        <ReactMarkdown>{msg.messageData.summary}</ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* {msg.messageData.summary && (
                    <div className="sql-chat-summary mb-4 text-sm text-gray-700 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                      <strong>Insight:</strong>
                      <ReactMarkdown className="mt-1">{msg.messageData.summary}</ReactMarkdown>
                    </div>
                  )} */}

                 

                  <div className="sql-chat-table">
                    <table>
                      <thead>
                        <tr>
                          {msg.messageData.columns.map((col, i) => (
                            <th key={i}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {msg.messageData.rows.map((row, r) => (
                          <tr key={r}>
                            {row.map((cell, c) => (
                              <td key={c}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {msg.messageData?.chart_config &&
                    typeof msg.messageData.chart_config.type === "string" &&
                    msg.messageData.chart_config.data &&
                    msg.messageData.chart_config.options && (
                      <div
                        className="sql-chat-chart"
                        style={{ marginTop: "20px", height: "40dvh", position: "relative" }}
                      >
                        <Suspense fallback={<div>Loading chart...</div>}>
                          <Chart
                            type={msg.messageData.chart_config.type}
                            data={msg.messageData.chart_config.data}
                            options={{
                              ...msg.messageData.chart_config.options,
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                title: {
                                  display: true,
                                  text: "Generated Chart",
                                },
                                ...(msg.messageData.chart_config.options.plugins || {}),
                              },
                            }}
                          />
                        </Suspense>
                      </div>
                    )}
                </div>
              )}
            </div>
          );
        })}
        <div ref={chatRef}></div>
      </div>

      <div className="sql-input-group">
        <input
          className="sql-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask a database question..."
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <button className="sql-button" onClick={handleQuery} disabled={loading}>
          {loading ? "Loading..." : "Send"}
        </button>
      </div>
    </div>
  );
};

export default SQLQuery;


// import { useState, useEffect, useRef } from "react";
// import { useGlobalState } from "./GlobalStateContext";
// import { Chart } from "react-chartjs-2";
// import {
//   Chart as ChartJS,
//   BarElement,
//   LineElement,
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   Title,
//   Tooltip,
//   Legend,
// } from "chart.js";
// import jsPDF from "jspdf";
// import html2canvas from "html2canvas";
// import "./SQLQuery.css";
// import { lazy, Suspense, memo } from "react";

// ChartJS.register(
//   BarElement,
//   LineElement,
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   Title,
//   Tooltip,
//   Legend
// );

// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

// const makeAuthorizedRequest = async (config) => {
//   try {
//     const token = localStorage.getItem("token");
//     const headers = {
//       ...config.headers,
//       Authorization: `Bearer ${token}`,
//     };

//     const response = await fetch(`${API_BASE_URL}${config.url}`, {
//       ...config,
//       headers,
//     });

//     if (response.status === 401) {
//       const refreshed = await attemptTokenRefresh();
//       if (refreshed) {
//         const retryToken = localStorage.getItem("token");
//         const retryHeaders = {
//           ...config.headers,
//           Authorization: `Bearer ${retryToken}`,
//         };
//         return await fetch(`${API_BASE_URL}${config.url}`, {
//           ...config,
//           headers: retryHeaders,
//         });
//       }
//     }

//     return response;
//   } catch (err) {
//     console.error("Request failed:", err);
//     throw err;
//   }
// };

// const attemptTokenRefresh = async () => {
//   const refreshToken = localStorage.getItem("refreshToken");
//   if (!refreshToken) return false;

//   try {
//     const response = await fetch(`${API_BASE_URL}/refresh`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ refresh_token: refreshToken }),
//     });

//     if (!response.ok) throw new Error("Refresh failed");

//     const data = await response.json();
//     localStorage.setItem("token", data.access_token);
//     return true;
//   } catch (error) {
//     console.error("Token refresh failed:", error);
//     localStorage.removeItem("token");
//     localStorage.removeItem("refreshToken");
//     window.location.reload();
//     return false;
//   }
// };

// const SQLQuery = () => {
//   const [query, setQuery] = useState("");
//   const [database, setDatabase] = useState("");
//   const [databases, setDatabases] = useState([]);
//   const { sqlChatHistory, setSqlChatHistory, sessionId, setSessionId } = useGlobalState();
//   const [loading, setLoading] = useState(false);
//   const chatRef = useRef(null);

//   // const Chart = lazy(() => import("react-chartjs-2").then((mod) => ({ default: mod.Chart })));
//   const Chart = memo(
//     lazy(() => import("react-chartjs-2").then((mod) => ({ default: mod.Chart })))
//   );
  



//   useEffect(() => {
//     const storedSessionId = localStorage.getItem("sessionId");
//     const id = storedSessionId || `session-${Math.random().toString(36).substr(2, 9)}`;
//     localStorage.setItem("sessionId", id);
//     setSessionId(id);

//     const savedHistory = localStorage.getItem(`sqlChatHistory_${id}`);
//     if (savedHistory) {
//       setSqlChatHistory(JSON.parse(savedHistory));
//     }
//   }, [setSessionId, setSqlChatHistory]);

//   useEffect(() => {
//     chatRef.current?.scrollIntoView({ behavior: "smooth" });
//   }, [sqlChatHistory]);

//   useEffect(() => {
//     if (sessionId) {
//       localStorage.setItem(`sqlChatHistory_${sessionId}`, JSON.stringify(sqlChatHistory));
//     }
//   }, [sqlChatHistory, sessionId]);

//   useEffect(() => {
//     const fetchDatabases = async () => {
//       try {
//         const response = await makeAuthorizedRequest({
//           url: "/admin/databases",
//           method: "GET",
//         });
//         if (!response.ok) throw new Error("Failed to fetch databases");

//         const data = await response.json();
//         setDatabases(data);
//         if (data.length > 0) setDatabase(data[0].name);
//       } catch (error) {
//         console.error("Database fetch error:", error);
//       }
//     };

//     fetchDatabases();
//   }, []);

//   const handleQuery = async () => {
//     if (!query.trim() || !database) return;

//     setSqlChatHistory((prev) => [
//       ...prev,
//       { type: "user", messageType: "text", messageData: query },
//       { type: "bot", messageType: "text", messageData: "Fetching results..." },
//     ]);
//     setQuery("");
//     setLoading(true);

//     try {
//       const response = await makeAuthorizedRequest({
//         url: "/tables_and_charts/",
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           session_id: sessionId,
//           database,
//           question: query,
//         }),
//       });

//       if (!response.ok) throw new Error(`Server error: ${response.status}`);

//       const result = await response.json();

//       const responseMessage = result.error
//         ? {
//             type: "bot",
//             messageType: "text",
//             messageData: `⚠️ Error: ${result.error}`,
//           }
//         : {
//             type: "bot",
//             messageType: "tableChart",
//             messageData: {
//               columns: result.columns,
//               rows: result.data,
//               chart_config: result.chart_config || null,
//             },
//           };

//       setSqlChatHistory((prev) => prev.slice(0, -1).concat(responseMessage));
//     } catch (error) {
//       console.error("Query error:", error);
//       setSqlChatHistory((prev) =>
//         prev.slice(0, -1).concat({
//           type: "error",
//           messageType: "text",
//           messageData: "❌ Error querying the database.",
//         })
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleKeyDown = (e) => {
//     if (e.key === "Enter") {
//       e.preventDefault();
//       handleQuery();
//     }
//   };

//   const handleExportPDF = async () => {
//     const input = document.getElementById("sql-chat-export");
//     if (!input) return;

//     const originalHeight = input.style.height;
//     input.style.height = `${input.scrollHeight}px`;

//     await new Promise((resolve) => setTimeout(resolve, 500));

//     const canvas = await html2canvas(input, {
//       scale: 2,
//       useCORS: true,
//       scrollY: -window.scrollY,
//       backgroundColor: "#ffffff",
//     });

//     input.style.height = originalHeight;

//     const imgData = canvas.toDataURL("image/png");
//     const pdf = new jsPDF("p", "mm", "a4");

//     const pdfWidth = pdf.internal.pageSize.getWidth();
//     const imgProps = pdf.getImageProperties(imgData);
//     const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
//     const pageHeight = pdf.internal.pageSize.getHeight();

//     let heightLeft = imgHeight;
//     let position = 0;

//     while (heightLeft > 0) {
//       pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
//       heightLeft -= pageHeight;
//       if (heightLeft > 0) {
//         position -= pageHeight;
//         pdf.addPage();
//       }
//     }

//     pdf.save("chat_output.pdf");
//   };

//   return (
//     <div className="sql-query-container">
//       <h2 className="sql-title">Tables & Charts (SQL)</h2>

//       <div className="sql-database-select-container">
//         <p className="sql-label">Select Database:</p>
//         <div className="sql-database-options">
//           {databases.map((db) => (
//             <label key={db.id} className="sql-database-option">
//               <input
//                 type="radio"
//                 name="database"
//                 value={db.name}
//                 checked={database === db.name}
//                 onChange={(e) => setDatabase(e.target.value)}
//               />
//               {db.name}
//             </label>
//           ))}
//         </div>
//       </div>

//       <div className="sql-export-button" style={{ textAlign: "right", marginBottom: "10px" }}>
//         <button onClick={handleExportPDF} className="export-pdf-button">
//           📄 Export as PDF
//         </button>
//       </div>

//       <div className="sql-chat-container" id="sql-chat-export">
//         {sqlChatHistory.map((msg, index) => (
//           <div key={index} className={`sql-chat-message ${msg.type}`}>
//             {msg.messageType === "text" && (
//               <p className="whitespace-pre-wrap">{msg.messageData}</p>
//             )}

//             {msg.messageType === "tableChart" && (
//               <div className="sql-chat-response">
//                 <div className="sql-chat-table">
//                   <table>
//                     <thead>
//                       <tr>
//                         {msg.messageData.columns.map((col, i) => (
//                           <th key={i}>{col}</th>
//                         ))}
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {msg.messageData.rows.map((row, r) => (
//                         <tr key={r}>
//                           {row.map((cell, c) => (
//                             <td key={c}>{cell}</td>
//                           ))}
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>

                
//                 {msg.messageType === "tableChart" && (
//                   <div className="sql-chat-response">
//                     {/* ... table code ... */}
//                     {msg.messageData?.chart_config &&
//                     typeof msg.messageData.chart_config.type === "string" &&
//                     msg.messageData.chart_config.data &&
//                     msg.messageData.chart_config.options ? (
//                       <div
//                         className="sql-chat-chart"
//                         style={{ marginTop: "20px", height: "40dvh", position: "relative" }}
//                       >
//                         <Suspense fallback={<div>Loading chart...</div>}>
//                           <Chart
//                             type={msg.messageData.chart_config.type}
//                             data={msg.messageData.chart_config.data}
//                             options={{
//                               ...msg.messageData.chart_config.options,
//                               responsive: true,
//                               maintainAspectRatio: false,
//                               plugins: {
//                                 title: {
//                                   display: true,
//                                   text: "Generated Chart",
//                                 },
//                                 ...(msg.messageData.chart_config.options.plugins || {}),
//                               },
//                             }}
//                           />
//                         </Suspense>
//                       </div>
//                     ) : null}
//                   </div>
//                 )}

          


      


//               </div>
//             )}
//           </div>
//         ))}
//         <div ref={chatRef}></div>
//       </div>

//       <div className="sql-input-group">
//         <input
//           className="sql-input"
//           value={query}
//           onChange={(e) => setQuery(e.target.value)}
//           placeholder="Ask a database question..."
//           onKeyDown={handleKeyDown}
//           disabled={loading}
//         />
//         <button className="sql-button" onClick={handleQuery} disabled={loading}>
//           {loading ? "Loading..." : "Send"}
//         </button>
//       </div>
//     </div>
//   );
// };

// export default SQLQuery;
